import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, ShieldAlert, Trash2 } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type { ReleaseDetail, ReleaseKnownIssue } from "../../shared/types/api";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  InlineAlert,
  LoadingState,
  StatusBadge,
} from "../../shared/ui";
import { KnownIssueDrawer } from "./KnownIssueDrawer";
import { useReleaseMutation } from "./useReleaseMutation";
import {
  SEVERITY_LABELS,
  SEVERITY_TONE,
  blocksRelease,
  describeKnownIssue,
} from "./releaseVocabulary";

/**
 * What is known to be broken, and who agreed to ship with it (§30).
 *
 * <p>This tab is what makes the critical-bugs gate mean anything. An unaccepted blocker here
 * fails that gate by name; accepting the risk is what clears it, and the issue stays on the
 * release afterwards. The alternative — deleting the row to get the release moving — is exactly
 * what an incident review must never find.
 */
export function ReleaseKnownIssuesTab({
  release,
  onChanged,
}: {
  release: ReleaseDetail;
  onChanged: () => void;
}) {
  const { can } = useAuth();
  const [editing, setEditing] = useState<ReleaseKnownIssue | null>(null);
  const [mode, setMode] = useState<"edit" | "accept">("edit");
  const [open, setOpen] = useState(false);
  const { run, saving } = useReleaseMutation(release.id);

  const issues = useQuery({
    queryKey: ["release-known-issues", release.id],
    queryFn: () =>
      api.get<ReleaseKnownIssue[]>(`/api/v1/releases/${release.id}/known-issues`),
  });

  const rows = issues.data ?? [];
  const holding = rows.filter((row) => blocksRelease(row.severity) && !row.riskAccepted);

  const openDrawer = (issue: ReleaseKnownIssue | null, next: "edit" | "accept") => {
    setEditing(issue);
    setMode(next);
    setOpen(true);
  };

  const remove = (issue: ReleaseKnownIssue) =>
    void run(
      () => api.del(`/api/v1/releases/${release.id}/known-issues/${issue.id}`),
      onChanged,
    );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Known issues"
          subtitle={
            rows.length === 0
              ? "Nothing recorded as broken."
              : `${rows.length} recorded · ${holding.length} holding the release`
          }
          action={
            can("RELEASE_EDIT") &&
            rows.length > 0 && (
              <Button
                size="sm"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => openDrawer(null, "edit")}
              >
                Record an issue
              </Button>
            )
          }
        />
        <CardBody className="space-y-3">
          {holding.length > 0 && (
            <InlineAlert tone="error">
              {holding.length === 1
                ? "1 blocker or critical issue has not been accepted."
                : `${holding.length} blocker or critical issues have not been accepted.`}{" "}
              The critical-bugs gate fails until somebody with override rights accepts the risk —
              in writing, with their name on it.
            </InlineAlert>
          )}

          {issues.isPending ? (
            <LoadingState label="Loading known issues…" />
          ) : rows.length === 0 ? (
            <EmptyState
              title="Nothing known to be broken"
              message="Shipping with a known defect is a legitimate decision. Shipping with one nobody named is not — this is where the first kind gets written down."
              action={
                can("RELEASE_EDIT") ? (
                  <Button onClick={() => openDrawer(null, "edit")}>Record an issue</Button>
                ) : undefined
              }
            />
          ) : (
            <ul className="divide-y divide-ink-100">
              {rows.map((row) => (
                <li key={row.id} className="flex items-start justify-between gap-3 py-2.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink-900">
                      {row.title}
                      {row.workItemKey && (
                        <span className="ml-2 font-mono text-xs font-normal text-ink-500">
                          {row.workItemKey}
                        </span>
                      )}
                    </p>
                    <p className="mt-0.5 text-xs text-ink-500">{describeKnownIssue(row)}</p>
                    {row.impact && (
                      <p className="mt-1 text-xs text-ink-600">{row.impact}</p>
                    )}
                    {row.workaround && (
                      <p className="mt-1 text-xs text-ink-600">
                        <span className="text-ink-400">Workaround: </span>
                        {row.workaround}
                      </p>
                    )}
                    {row.riskAccepted && row.comment && (
                      <p className="mt-1 rounded-md border-l-2 border-amber-300 bg-amber-50 px-2 py-1 text-xs text-amber-900">
                        {row.comment}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <StatusBadge tone={SEVERITY_TONE[row.severity]}>
                      {SEVERITY_LABELS[row.severity]}
                    </StatusBadge>
                    {row.riskAccepted ? (
                      <StatusBadge tone="amber">Risk accepted</StatusBadge>
                    ) : (
                      <>
                        {can("RELEASE_GATE_OVERRIDE") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<ShieldAlert className="h-4 w-4" />}
                            onClick={() => openDrawer(row, "accept")}
                          >
                            Accept risk
                          </Button>
                        )}
                        {can("RELEASE_EDIT") && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDrawer(row, "edit")}
                            >
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              loading={saving}
                              icon={<Trash2 className="h-4 w-4" />}
                              aria-label={`Remove ${row.title}`}
                              onClick={() => remove(row)}
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <KnownIssueDrawer
        release={release}
        issue={editing}
        mode={mode}
        open={open}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        onSaved={onChanged}
      />
    </div>
  );
}
